import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './portal.css';
import { clearPortalToken, getPortalSite, getPortalToken, portalApi, setPortalSiteStored, setPortalToken } from './portalApi';
import { Spinner } from './portalUi';
import PortalLayout from './PortalLayout';
import PortalLogin from './PortalLogin';
import PortalSetPassword from './PortalSetPassword';
import PortalOverview from './PortalOverview';
import PortalSchedule from './PortalSchedule';
import PortalHours from './PortalHours';
import PortalInvoices from './PortalInvoices';
import PortalSafety from './PortalSafety';
import PortalPhotos from './PortalPhotos';
import PortalInspections from './PortalInspections';
import PortalMessages from './PortalMessages';
import PortalRequest from './PortalRequest';

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

const RequireLogin = () => {
  const loc = useLocation();
  return <Navigate to="/portal/login" replace state={{ from: loc.pathname + loc.search }} />;
};

export default function PortalApp() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(!!getPortalToken());
  const [site, setSiteState] = useState(getPortalSite());

  const reload = useCallback(async () => {
    if (!getPortalToken()) { setMe(null); setLoading(false); return; }
    try {
      const r = await portalApi.get('/auth/me');
      setMe(r.data);
      // drop a remembered site that no longer exists on this customer (or when there's only one plant → nothing to narrow)
      const names = (r.data?.customer?.sites || []).map((s) => s.name);
      if (getPortalSite() && (names.length < 2 || !names.includes(getPortalSite()))) { setPortalSiteStored(''); setSiteState(''); }
    } catch (e) {
      clearPortalToken();
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const setSite = (s) => { setPortalSiteStored(s || ''); setSiteState(s || ''); };
  const setSession = (data) => {
    setPortalToken(data.access_token);
    setMe({ user: data.user, customer: data.customer });
    reload();
  };
  const logout = () => { clearPortalToken(); setMe(null); setSiteState(''); };

  if (loading) return <div className="portal-root"><Spinner /></div>;

  return (
    <PortalCtx.Provider value={{ me, setSession, logout, reload, site, setSite }}>
      <div className="portal-root">
        <Routes>
          <Route path="login" element={me ? <Navigate to="/portal" replace /> : <PortalLogin />} />
          <Route path="set-password" element={<PortalSetPassword />} />
          <Route element={me ? <PortalLayout /> : <RequireLogin />}>
            <Route index element={<PortalOverview />} />
            <Route path="schedule" element={<PortalSchedule />} />
            <Route path="hours" element={<PortalHours />} />
            <Route path="invoices" element={<PortalInvoices />} />
            <Route path="safety" element={<PortalSafety />} />
            <Route path="photos" element={<PortalPhotos />} />
            <Route path="inspections" element={<PortalInspections />} />
            <Route path="messages" element={<PortalMessages />} />
            <Route path="request" element={<PortalRequest />} />
          </Route>
          <Route path="*" element={<Navigate to="/portal" replace />} />
        </Routes>
      </div>
    </PortalCtx.Provider>
  );
}
